import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Grid,
  TextField,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TablePagination,
  Chip,
  FormControlLabel,
  Switch,
  Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import SnackbarWrapper from "components/Snackbar";
import LoadingScreen from "components/Loading";
import { useAuth } from "context/authContext";
import AssignCompensationDialog from "components/AssignCompensationDialog";
import {
  getEmployeesBasicInfo,
  getEmployeesPaging,
  getEmployeesWithoutCompensation,
  getJobs,
  getPayrollStructures,
  getActiveCompensation,
  assignCompensation,
} from "services/api";

const AssignCompensationView = ({ canEdit }) => {
  const { t } = useTranslation();
  const { employee: loggedInEmployee } = useAuth();
  const snackbarRef = useRef();

  const [loading, setLoading] = useState(true);

  // Full lightweight list - only powers the dialog's Employee/Approver pickers
  const [pickerEmployees, setPickerEmployees] = useState([]);

  const [jobs, setJobs] = useState([]);
  const [structures, setStructures] = useState([]);

  const [pagedEmployees, setPagedEmployees] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tableLoading, setTableLoading] = useState(false);

  // Filters
  const [jobFilter, setJobFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // Only meaningful when a job is selected - there's no backend endpoint yet
  // for "everyone without compensation across every job, paginated," only
  // the job-scoped one we already have.
  const [hideAssigned, setHideAssigned] = useState(false);

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [activeCompensation, setActiveCompensation] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const loadStatic = async () => {
      setLoading(true);
      const [pickerRes, jobsRes, structuresRes] = await Promise.all([
        getEmployeesBasicInfo(),
        getJobs(),
        getPayrollStructures(),
      ]);
      if (pickerRes !== "error") setPickerEmployees(pickerRes);
      if (jobsRes !== "error") setJobs(jobsRes);
      if (structuresRes !== "error") setStructures(structuresRes);
      setLoading(false);
    };
    loadStatic();
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    setPage(0);
  }, [jobFilter, debouncedSearch, hideAssigned]);

  // The hideAssigned toggle can't do anything without a job selected. Turn
  // it back off automatically if the job filter is cleared, so it never
  // sits on looking active while silently doing nothing.
  useEffect(() => {
    if (!jobFilter && hideAssigned) {
      setHideAssigned(false);
    }
  }, [jobFilter]);

  const fetchPage = useCallback(async () => {
    setTableLoading(true);

    if (hideAssigned && jobFilter) {
      // Job-scoped "uncovered" list - not paginated on the backend, so we
      // paginate and search it client-side. In practice the uncovered set
      // for a single job is a bounded, reasonably small list.
      const res = await getEmployeesWithoutCompensation(jobFilter.jobId);
      if (res === "error") {
        setPagedEmployees([]);
        setTotalElements(0);
      } else {
        const filtered = debouncedSearch
          ? res.filter((e) => e.empName?.toLowerCase().includes(debouncedSearch.toLowerCase()))
          : res;
        setTotalElements(filtered.length);
        setPagedEmployees(filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage));
      }
    } else {
      const res = await getEmployeesPaging(
        page,
        rowsPerPage,
        jobFilter?.jobId || undefined,
        undefined,
        undefined,
        debouncedSearch || undefined
      );

      if (res === "error") {
        setPagedEmployees([]);
        setTotalElements(0);
      } else {
        // Defensive: handle both a standard Spring Page ({ content, totalElements })
        // and a plain array.
        const content = Array.isArray(res) ? res : res?.content || [];
        const total = Array.isArray(res) ? res.length : (res?.totalElements ?? content.length);
        setPagedEmployees(content);
        setTotalElements(total);
      }
    }

    setTableLoading(false);
  }, [page, rowsPerPage, jobFilter, debouncedSearch, hideAssigned]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const structureName = (structureId) => {
    const match = structures.find((s) => s.id === structureId);
    return match ? match.name : `#${structureId}`;
  };

  const handleSelectEmployee = async (emp) => {
    setSelectedEmployee(emp);
    setActiveCompensation(null);
    setLoadingDetail(true);
    const res = await getActiveCompensation(emp.empId);
    // Backend returns an Optional - empty means null body with 200, not an error
    setActiveCompensation(res && res !== "error" ? res : null);
    setLoadingDetail(false);
  };

  const handleSubmitAssignment = async (form) => {
    console.log("Submitting assignment", form);
    const res = await assignCompensation({
      ...form,
      createdByStr: loggedInEmployee?.id,
    });

    if (res !== "error" && typeof res !== "string") {
      snackbarRef.current?.showSnackbar(t("admin.payroll.employees.assignSuccess"), "success");
      setDialogOpen(false);
      if (selectedEmployee && selectedEmployee.empId === form.employeeId) {
        setActiveCompensation(res);
      }
      // The employee that was just assigned should drop off an "unassigned"
      // view - refresh the table directly rather than relying on effect
      // dependency tricks.
      fetchPage();
    } else {
      snackbarRef.current?.showSnackbar(
        typeof res === "string" ? res : t("admin.payroll.employees.assignError"),
        "error"
      );
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={4}>
            <Autocomplete
              options={jobs}
              getOptionLabel={(job) => job.jobName || ""}
              isOptionEqualToValue={(option, value) => option.jobId === value.jobId}
              value={jobFilter}
              onChange={(event, newValue) => setJobFilter(newValue)}
              renderInput={(params) => (
                <TextField {...params} label={t("admin.payroll.employees.filterByJob")} />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              label={t("admin.payroll.employees.searchEmployee")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            {canEdit && (
              <MDButton color="primary" fullWidth onClick={() => setDialogOpen(true)}>
                {t("admin.payroll.employees.assignButton")}
              </MDButton>
            )}
          </Grid>
        </Grid>

        <Grid container sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Tooltip
              title={!jobFilter ? t("admin.payroll.employees.hideAssignedNeedsJob") : ""}
              disableHoverListener={!!jobFilter}
            >
              <span>
                <FormControlLabel
                  control={
                    <Switch
                      checked={hideAssigned}
                      onChange={(e) => setHideAssigned(e.target.checked)}
                      disabled={!jobFilter}
                    />
                  }
                  label={t("admin.payroll.employees.hideAssigned")}
                />
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={selectedEmployee ? 7 : 12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            {tableLoading ? (
              <LoadingScreen />
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          {t("admin.payroll.employees.employee")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          {t("admin.payroll.employees.job")}
                        </TableCell>
                      </TableRow>
                      {pagedEmployees.map((emp) => (
                        <TableRow
                          key={emp.empId}
                          hover
                          selected={selectedEmployee?.empId === emp.empId}
                          onClick={() => handleSelectEmployee(emp)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell>{emp.empName}</TableCell>
                          <TableCell>{emp.jobName || t("admin.payroll.employees.noJob")}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={totalElements}
                  page={page}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50]}
                />
              </>
            )}
          </Paper>
        </Grid>

        {selectedEmployee && (
          <Grid item xs={12} md={5}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <MDTypography variant="subtitle2">{selectedEmployee.empName}</MDTypography>
              <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 2 }}>
                {selectedEmployee.jobName || t("admin.payroll.employees.noJob")}
              </MDTypography>

              {loadingDetail ? (
                <LoadingScreen />
              ) : activeCompensation ? (
                <MDBox>
                  <MDTypography variant="body2">
                    {structureName(activeCompensation.payrollStructureId)}
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    {t("admin.payroll.employees.since", { date: activeCompensation.effectiveFrom })}{" "}
                    · {activeCompensation.currency}
                  </MDTypography>
                  <Chip
                    size="small"
                    label={t("admin.payroll.employees.active")}
                    color="success"
                    sx={{ ml: 1 }}
                  />
                </MDBox>
              ) : (
                <MDTypography variant="body2" color="text">
                  {t("admin.payroll.employees.notOnboarded")}
                </MDTypography>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>

      <AssignCompensationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmitAssignment}
        employees={pickerEmployees}
        payrollStructures={structures}
        approvers={pickerEmployees}
        initialEmployeeId={selectedEmployee?.empId || ""}
      />

      <SnackbarWrapper
        ref={snackbarRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </Box>
  );
};

AssignCompensationView.propTypes = {
  canEdit: PropTypes.bool.isRequired,
};

export default AssignCompensationView;
